import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { SearchBox } from "../../../components/Search";
// ▶ DEMO — demo const path
import { formatDateForAPI } from "../const";
// ▶ DEMO — demo API imports
import {
  getrestrictedAreaGssLoadDaily,
  getGssrestrictedAreaLoadMonthly,
  getRestrictedGssLoadDailyDiscom,
  getRestrictedMonthlyDiscomGssLoad,
  getGssLoadRestrictedDistrictDailyDiscom,
  getMonthlyRestrictedDistrictDiscomGssLoad,
} from "../../../Rest_api/DemoRestApi";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import MetricChip from "../../../components/Metrics";
import { getRestrictedLoadMetrics } from "../../Rajasthan/Utility/RestApi";
import { DiscomLoadMetrics } from "../../../models/metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { normalizeDisplayLabel } from "../../../utility/displayText";
const X_AXIS = [
  { label: "Date", key: "date", values: ["date"], isTime: false },
  { label: "Month", key: "month", values: ["month"], isTime: false },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = ["MW", "MWh", "LU"];

const GROUP_BY = [
  { label: "Area", key: "area", values: ["area", "discom"] },
  { label: "Discom", key: "discom", values: ["discom"] },
  { label: "District", key: "district", values: ["district", "discom"] },
];

// ▶ DEMO — masked discom names
const DISCOM = [
  { label: "Discom 1", key: "Discom 1", apiKey: "Discom 1" },
  { label: "Discom 2", key: "Discom 2", apiKey: "Discom 2" },
  { label: "Discom 3", key: "Discom 3", apiKey: "Discom 3" },
];

function getApiDiscomKeys(displayKeys: string[]): string[] {
  return displayKeys.map((displayKey) => {
    const discom = DISCOM.find((d) => d.key === displayKey);
    return discom ? discom.apiKey : displayKey;
  });
}

function getDisplayDiscomName(apiKey: string): string {
  const discom = DISCOM.find((d) => d.apiKey === apiKey);
  return discom ? discom.label : apiKey;
}

function parseMonthYear(monthYearStr: string | number | undefined): {
  month: number;
  year: number;
} {
  if (!monthYearStr) return { month: 0, year: 0 };
  if (typeof monthYearStr === "number") return { month: monthYearStr, year: 0 };
  if (typeof monthYearStr === "string") {
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const parts = monthYearStr.split("-");
    if (parts.length !== 2) return { month: 0, year: 0 };
    const [monthStr, yearStr] = parts;
    const month = monthNames.indexOf(monthStr) + 1;
    const year = 2000 + parseInt(yearStr);
    return { month, year };
  }
  return { month: 0, year: 0 };
}

interface RajasthanDrawalPeakAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  selectedGroupKeys: any[];
  onGroupByChange: (index: number) => void;
  selectedArea: any[];
  onAreaChange: (v: any) => void;
  selectedDistricts: any[];
  onDistrictsChange: (v: any) => void;
  metrics: DiscomLoadMetrics | null;
}

export function RajasthanDrawalPeakAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedDiscom,
  onDiscomChange,
  selectedGroupKeys,
  onGroupByChange,
  selectedArea,
  onAreaChange,
  selectedDistricts,
  onDistrictsChange,
  metrics,
}: RajasthanDrawalPeakAnalysisProps) {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [data, setData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [loading, setLoading] = useState(true);
  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [uniqueArea, setUniqueArea] = useState<any[]>([]);
  const [uniqueDistricts, setUniqueDistricts] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);

  // ─── Fetch ────────────────────────────────────────────────────────────────
  // ▶ DEMO — single `data` state + conditional fetch (prod fetches all 3 in parallel)
  const _handlefetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (DefaultDates[activeTab].defaultRangeDate === undefined) return;
      const dates = DefaultDates[activeTab].defaultRangeDate;
      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      let allData: any[] = [];

      const isAreaGrouping = selectedGroupKeys[0]?.key === "area";
      const isDiscomGrouping = selectedGroupKeys[0]?.key === "discom";
      const isDistrictGrouping = selectedGroupKeys[0]?.key === "district";

      if (selectedXaxis.key === "month") {
        if (isAreaGrouping) {
          const monthData = await getGssrestrictedAreaLoadMonthly(
            startDateStr,
            endDateStr,
          );
          if (Array.isArray(monthData)) allData = monthData;
        } else if (isDiscomGrouping) {
          const monthlyDiscomData = await getRestrictedMonthlyDiscomGssLoad(
            startDateStr,
            endDateStr,
          );
          if (Array.isArray(monthlyDiscomData)) allData = monthlyDiscomData;
        } else if (isDistrictGrouping) {
          const monthlyDistrictData =
            await getMonthlyRestrictedDistrictDiscomGssLoad(
              startDateStr,
              endDateStr,
            );
          if (Array.isArray(monthlyDistrictData)) allData = monthlyDistrictData;
        }
      } else {
        if (isAreaGrouping) {
          const dailyData = await getrestrictedAreaGssLoadDaily(
            startDateStr,
            endDateStr,
          );
          if (Array.isArray(dailyData)) allData = dailyData;
        } else if (isDiscomGrouping) {
          const discomData = await getRestrictedGssLoadDailyDiscom(
            startDateStr,
            endDateStr,
          );
          if (Array.isArray(discomData)) allData = discomData;
        } else if (isDistrictGrouping) {
          const districtData = await getGssLoadRestrictedDistrictDailyDiscom(
            startDateStr,
            endDateStr,
          );
          if (Array.isArray(districtData)) allData = districtData;
        }
      }

      if (isDistrictGrouping && allData.length > 0) {
        allData = allData.map((item) => {
          const normalized: any = { ...item };
          if (
            !normalized.district ||
            normalized.district === "undefined" ||
            normalized.district === "null"
          ) {
            normalized.district = item.discom;
          }
          if (item.peak_load_mw !== undefined && !item.daily_peak_load_mw) {
            normalized.daily_peak_load_mw = item.peak_load_mw;
          }
          if (item.average_load_mw !== undefined && !item.avg_daily_load_mw) {
            normalized.avg_daily_load_mw = item.average_load_mw;
          }
          if (
            selectedXaxis.key === "month" &&
            typeof item.month === "number" &&
            item.year
          ) {
            const monthNames = [
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec",
            ];
            const yearShort = item.year.toString().slice(-2);
            normalized.month = `${monthNames[item.month - 1]}-${yearShort}`;
            normalized.monthNumber = item.month;
            normalized.year = item.year;
          }
          return normalized;
        });
      }

      setData(allData);

      if (selectedXaxis.key === "date") {
        setUniqueDate(
          Array.from(
            new Set(
              allData
                .map((item: any) => item.date)
                .filter((date: any) => date !== undefined)
                .sort(
                  (a: string, b: string) =>
                    new Date(a).getTime() - new Date(b).getTime(),
                ),
            ),
          ),
        );
      } else {
        const uniqueMonths = Array.from(
          new Set(
            allData
              .map((item: any) => item.month)
              .filter((m: any) => m !== undefined),
          ),
        ).sort((a: string, b: string) => {
          const am = parseMonthYear(a);
          const bm = parseMonthYear(b);
          if (am.year !== bm.year) return am.year - bm.year;
          return am.month - bm.month;
        });
        setUniqueDate(uniqueMonths);
      }
    } catch (error) {
      console.error("Error in data fetching:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab, selectedXaxis.key, selectedGroupKeys]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  // ─── Process ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (data.length === 0) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    let filteredData = data;

    if (selectedDiscom.length > 0) {
      const apiDiscomKeys = getApiDiscomKeys(selectedDiscom);
      filteredData = filteredData.filter((item) =>
        apiDiscomKeys.includes(item.discom),
      );
    }

    if (selectedDate.length > 0) {
      if (selectedXaxis.key === "date") {
        filteredData = filteredData.filter((item) =>
          selectedDate.map((d) => d.value).includes(item.date),
        );
      } else {
        filteredData = filteredData.filter((item) =>
          selectedDate.map((d) => d.value).includes(item.month),
        );
      }
    }

    const districtLabels = Array.from(
      new Set(
        filteredData.map((item) => {
          const name =
            item.district || item.divisional_headquarter || item.discom;
          const discomName = getDisplayDiscomName(item.discom);
          return `${name} (${discomName})`;
        }),
      ),
    )
      .filter(
        (label) =>
          label && !label.includes("undefined") && !label.includes("null"),
      )
      .sort();
    setUniqueDistricts(districtLabels);

    if (selectedDistricts.length > 0) {
      const distStrings = selectedDistricts.map((d: any) => d.value);
      filteredData = filteredData.filter((item) => {
        const name =
          item.district || item.divisional_headquarter || item.discom;
        const discomName = getDisplayDiscomName(item.discom);
        return distStrings.includes(`${name} (${discomName})`);
      });
    }

    const areaLabels = Array.from(
      new Set(
        filteredData.map((item) => {
          const name = item.area || item.station;
          const discomName = getDisplayDiscomName(item.discom);
          return `${name} (${discomName})`;
        }),
      ),
    )
      .filter(
        (label) =>
          label &&
          !label.includes("undefined") &&
          !label.includes("null") &&
          !label.startsWith("UNKNOWN"),
      )
      .sort();
    setUniqueArea(areaLabels);

    if (selectedArea.length > 0) {
      const areaStrings = selectedArea.map((a: any) => a.value);
      filteredData = filteredData.filter((item) => {
        const name = item.area || item.station;
        const discomName = getDisplayDiscomName(item.discom);
        return areaStrings.includes(`${name} (${discomName})`);
      });
    }

    filteredData.sort((a, b) => {
      if (selectedXaxis.key === "month") {
        if (!a.month || !b.month) return 0;
        const am = parseMonthYear(a.month);
        const bm = parseMonthYear(b.month);
        const aYear = am.year || a.year || 0;
        const bYear = bm.year || b.year || 0;
        if (aYear !== bYear) return aYear - bYear;
        return am.month - bm.month;
      } else {
        if (!a.date || !b.date) return 0;
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      }
    });

    if (selectedGroupKeys[0]?.key === "district") {
      filteredData = filteredData.map((item) => {
        let districtValue =
          item.district || item.divisional_headquarter || item.div_hq;
        if (
          !districtValue ||
          districtValue === "undefined" ||
          districtValue === "null" ||
          String(districtValue).trim() === ""
        ) {
          districtValue = item.discom;
        }
        return { ...item, district: districtValue };
      });
    }

    const conversionFactor =
      selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;

    const convertedData = filteredData.map((item) => ({
      ...item,
      daily_peak_load_mw: (item.daily_peak_load_mw || 0) * conversionFactor,
      morning_peak_load_mw: (item.morning_peak_load_mw || 0) * conversionFactor,
      evening_peak_load_mw: (item.evening_peak_load_mw || 0) * conversionFactor,
      avg_daily_load_mw: (item.avg_daily_load_mw || 0) * conversionFactor,
    }));

    let groupByKeys: string[] = [];
    if (selectedGroupKeys[0]?.key === "area") groupByKeys = ["area", "discom"];
    else if (selectedGroupKeys[0]?.key === "discom") groupByKeys = ["discom"];
    else if (selectedGroupKeys[0]?.key === "district")
      groupByKeys = ["district", "discom"];

    const par: ChartDataParams = {
      keys: selectedXaxis.values,
      tableKeys: selectedXaxis.key === "date" ? ["date"] : ["month", "year"],
      groupBy: groupByKeys,
      volumeKey: "",
      aggregationKeys: [
        "daily_peak_load_mw",
        "morning_peak_load_mw",
        "evening_peak_load_mw",
      ],
      averageKeys: [],
      averageDuplicationKeys: ["date"],
      priceKey: "",
      data: convertedData,
    };

    const cData = FormatChartData(par);
    setTableData(cData.tableData || []);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [
    data,
    selectedXaxis,
    selectedUnit,
    selectedDate,
    selectedDiscom,
    selectedGroupKeys,
    selectedArea,
    selectedDistricts,
  ]);

  // ─── Legends ──────────────────────────────────────────────────────────────
  const legends = useMemo(() => {
    const peakTypes = [
      { field: "daily_peak_load", displayName: "Day Peak" },
      { field: "morning_peak_load", displayName: "Morning Peak" },
      { field: "evening_peak_load", displayName: "Evening Peak" },
    ];

    if (!chartKeys?.length) return [];

    return chartKeys.flatMap((key) => {
      let displayKey = key;

      if (selectedGroupKeys[0]?.key === "area") {
        if (key.includes("_")) {
          const parts = key.split("_");
          const discomKey = parts[parts.length - 1];
          const stationOrAreaKey = parts.slice(0, -1).join("_");
          displayKey =
            stationOrAreaKey.toLowerCase() === "rajasthan"
              ? discomKey
              : `${stationOrAreaKey} (${discomKey})`;
        } else {
          displayKey = getDisplayDiscomName(key);
        }
      } else if (selectedGroupKeys[0]?.key === "discom") {
        // ▶ DEMO — key is already "Discom_1" → restore spaces
        displayKey = normalizeDisplayLabel(key);
      } else if (selectedGroupKeys[0]?.key === "district") {
        if (key.includes("_")) {
          const parts = key.split("_");
          const discomKey = parts[parts.length - 1];
          const districtKey = parts.slice(0, -1).join("_");
          displayKey =
            !districtKey ||
            districtKey === "undefined" ||
            districtKey.trim() === ""
              ? discomKey
              : `${districtKey} (${discomKey})`;
        } else {
          displayKey = getDisplayDiscomName(key);
        }
      }

      return peakTypes.map((peak) => ({
        dataKey: `${key}_${peak.field}_mw`,
        name: `${displayKey} ${peak.displayName}`,
        legendColor: undefined,
        stroke: undefined,
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? peak.field : undefined,
      }));
    });
  }, [chartKeys, selectedGroupKeys, selectedChartType]);

  // ─── Table helpers ────────────────────────────────────────────────────────
  function summaryExtraKeys() {
    return selectedXaxis.key === "date" ? ["date"] : ["month", "year"];
  }

  const summaryExtraLabels =
    selectedXaxis.key === "date" ? ["Date"] : ["Month", "Year"];

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends, [chartData]);

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Restricted Demand Peak Analysis",
      dates: DefaultDates[activeTab]?.defaultRangeDate || [],
    }),
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Peak Analysis"]}
            onSelect={onXAxisModeChange}
          />
          <FilterMenu
            title="Intervals"
            items={X_AXIS.map((item) => item.label)}
            selectedItems={[selectedXaxis.label]}
            onSelect={(item, index) => {
              setSelectedXaxis(X_AXIS[index]);
              setSelectedDate([]);
            }}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => onUnitChange(item)}
          />
          <FilterMenu
            title="Discom"
            items={DISCOM.map((item) => item.label)}
            selectedItems={selectedDiscom}
            onSelect={() => {}}
            setSelectedItems={onDiscomChange}
          />
          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((item) => item.label)}
            selectedItems={selectedGroupKeys.map((item) => item.label)}
            onSelect={(item, index) => onGroupByChange(index)}
          />
          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) =>
              setSelectedChartType(H_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChip
              icon={<PeakIcon width={20} height={19} />}
              items={[
                {
                  label: "Peak Load",
                  value: metrics["Peak Load (MW)"].value,
                  unit: metrics["Peak Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<AvgIcon width={20} height={19} />}
              items={[
                {
                  label: "Average Load",
                  value: metrics["Average Load (MW)"].value,
                  unit: metrics["Average Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<MinIcon width={20} height={19} />}
              items={[
                {
                  label: "Minimum Load",
                  value: metrics["Minimum Load (MW)"].value,
                  unit: metrics["Minimum Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<TotalIcon width={20} height={19} />}
              items={[
                {
                  label: "Total Energy",
                  value: metrics["Total Energy (MU)"].value,
                  unit: metrics["Total Energy (MU)"].unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-100">
                  Restricted Demand Peak Analysis ({selectedUnit})
                </h2>
                <div className="flex gap-x-6 justify-end">
                  {selectedGroupKeys[0]?.key === "district" && (
                    <div className="min-w-60">
                      <SearchBox
                        options={uniqueDistricts.map((item: any) => ({
                          value: item,
                          label: item,
                        }))}
                        isMany={true}
                        placeholder="Search District"
                        selectedOptions={selectedDistricts}
                        onChange={(v: any) => {
                          onDistrictsChange(v);
                          onAreaChange([]);
                        }}
                      />
                    </div>
                  )}
                  {(selectedGroupKeys[0]?.key === "area" ||
                    (selectedGroupKeys[0]?.key === "district" &&
                      selectedDistricts.length > 0)) && (
                    <div className="min-w-60">
                      <SearchBox
                        options={uniqueArea.map((item) => ({
                          value: item,
                          label: item,
                        }))}
                        isMany={true}
                        placeholder="Search Area"
                        selectedOptions={selectedArea}
                        onChange={(v: any) => onAreaChange(v)}
                      />
                    </div>
                  )}
                  <button
                    onClick={() => {
                      if (dataTab.label === "Chart")
                        downloadChartTableCSV(tablePar);
                      else if (dataTab.label === "Summary")
                        downloadSummaryCSV(tablePar);
                      else if (dataTab.label === "Table")
                        downloadChartTableCSV(tablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {chartData.length === 0 && !loading && (
                <div
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#666",
                  }}
                >
                  No data available for the selected filters. Please adjust your
                  selection.
                </div>
              )}

              {dataTab.label === "Chart" && chartData.length > 0 && (
                <ReMixChart
                  data={chartData}
                  showBrush={undefined}
                  isTimeSlot={false}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  legends={legends.map((leg) => ({
                    ...leg,
                    type: selectedChartType.value,
                    stackId: selectedChartType.stackId ?? undefined,
                  }))}
                  xDataKey={selectedXaxis.values[0]}
                  secondXDataKey={undefined}
                />
              )}
              {dataTab.label === "Summary" && chartData.length > 0 && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && chartData.length > 0 && (
                <ChartTable {...tablePar} />
              )}
            </>
          )}
        </div>

        {/* ▶ DEMO — simplified footer (no Source tooltip, "Demo Version" label) */}
        <div className="gna-footer-container">
          <div className="grid grid-cols-2 justify-between gap-5">
            <p className="gna-footer-text text-start col-span-1">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Demo Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
